import cn from 'classnames'
import React, {
  forwardRef,
  ButtonHTMLAttributes,
  JSXElementConstructor,
  useRef,
} from 'react'
import mergeRefs from 'react-merge-refs'
import s from './Button.module.css'
import { LoadingDots } from '@components/ui'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string
  className?: string
  variant?:  'primaryXSmall' | 'primarySmall' | 'primaryMedium' | 'primaryLarge' | 'secondaryXSmall' | 'secondarySmall' | 'secondaryMedium' | 'secondaryLarge' | 'outlineChip' | 'outlineXSmallRounded' | 'outlineXSmall' | 'outlineSmall' | 'outlineMedium' | 'outlineLarge'
  active?: boolean
  type?: 'submit' | 'reset' | 'button'
  Component?: string | JSXElementConstructor<any>
  width?: string | number
  loading?: boolean
  disabled?: boolean
  editorial?: boolean
}

const Button: React.FC<ButtonProps> = forwardRef((props, buttonRef) => {
  const {
    className,
    variant = 'outlineSmall',
    children,
    active,
    width,
    loading = false,
    disabled = false,
    style = {},
    Component = 'button',
    ...rest
  } = props
  const ref = useRef<typeof Component>(null)

  const rootClassName = cn(
    s.root,
    {
      [s.primaryXSmall]: variant === 'primaryXSmall',
      [s.primarySmall]: variant === 'primarySmall',
      [s.primaryMedium]: variant === 'primaryMedium',
      [s.primaryLarge]: variant === 'primaryLarge',
      [s.secondaryXSmall]: variant === 'secondaryXSmall',
      [s.secondarySmall]: variant === 'secondarySmall',
      [s.secondaryMedium]: variant === 'secondaryMedium',
      [s.secondaryLarge]: variant === 'secondaryLarge',
      [s.outlineChip]: variant === 'outlineChip',
      [s.outlineXSmallRounded]: variant === 'outlineXSmallRounded',
      [s.outlineXSmall]: variant === 'outlineXSmall',
      [s.outlineSmall]: variant === 'outlineSmall',
      [s.outlineMedium]: variant === 'outlineMedium',
      [s.outlineLarge]: variant === 'outlineLarge',

    },
    className
  )

  return (
    <Component
      aria-pressed={active}
      data-variant={variant}
      ref={mergeRefs([ref, buttonRef])}
      className={rootClassName}
      disabled={disabled}
      style={{
        width,
        ...style,
      }}
      {...rest}
    >
      {!loading && (children)}
      {loading && (
        <i className={s.loadingItem}>
          <LoadingDots />
        </i>
      )}
    </Component>
  )
})

Button.displayName = 'Button';
export default Button
